// src/hooks/useTemplates.js
import { useState, useEffect, useCallback } from 'react';
import * as templateApi from '../models/templateAPI';

/**
 * Hook for managing multiple templates
 * @returns {Object} Templates and CRUD operations
 */
export const useTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTemplates = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await templateApi.fetchAllTemplates(options);
      setTemplates(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const createTemplate = async (templateData) => {
    setLoading(true);
    setError(null);
    try {
      const newTemplate = await templateApi.createTemplate(templateData);
      setTemplates(prev => [newTemplate, ...prev]);
      return newTemplate;
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to create template';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const updateTemplate = async (id, templateData) => {
    setLoading(true);
    setError(null);
    try {
      const updatedTemplate = await templateApi.updateTemplate(id, templateData);
      setTemplates(prev => 
        prev.map(template => 
          template.id === id ? updatedTemplate : template
        )
      );
      return updatedTemplate;
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to update template';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id) => {
    setLoading(true);
    setError(null);
    try {
      await templateApi.deleteTemplate(id);
      setTemplates(prev => prev.filter(template => template.id !== id));
      return true;
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to delete template';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return {
    templates,
    loading,
    error,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
};

/**
 * Hook for managing a single template
 * @param {string} id - Template ID
 * @returns {Object} Template data and operations
 */
export const useTemplate = (id) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTemplate = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await templateApi.fetchTemplateById(id);
      setTemplate(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch template');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchTemplate();
    }
  }, [id, fetchTemplate]);

  const updateTemplate = async (templateData) => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    try {
      const updatedTemplate = await templateApi.updateTemplate(id, templateData);
      setTemplate(updatedTemplate);
      return updatedTemplate;
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to update template';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return {
    template,
    loading,
    error,
    fetchTemplate,
    updateTemplate,
  };
};